import moment from "moment-timezone";
import { IChat } from "../types/IMessage";
import IUser from "../types/services/IUser";
import get from "lodash.get";
import {
  MediaList,
  PostType,
  PostStyle,
  IMedia,
  IPost,
} from "../types/services/IPost";
import { IAudienceMetrics } from "../types/services/IMetrics";
import { isProductionDeployment } from "./isProductionDeployment";

const ALPHA_STORAGE_HOST = "mosaic-vision-alpha";

function isAlphaStorageUrl(url: string): boolean {
  return url.includes(ALPHA_STORAGE_HOST);
}

function pickPreviewThumbnailUrl(preview: IMedia): string {
  const ordered = [
    preview.playPreviewText,
    preview.playPreview,
    preview.mmsImageUrl,
    preview.thumbnail,
    preview.mediaUrl,
  ];
  for (const url of ordered) {
    if (!url) {
      continue;
    }
    if (
      url === preview.mediaUrl &&
      isProductionDeployment() &&
      isAlphaStorageUrl(url)
    ) {
      continue;
    }
    return url;
  }
  return "";
}
export function getParsedDate(
  dateEl: string,
  isUTC: boolean = true,
  format: string = "YYYY-MM-DD"
): string {
  const createdDate = moment(dateEl);
  return isUTC ? createdDate.utc().format(format) : createdDate.format(format);
}

export function isValidDate(dateEl: string): boolean {
  return moment(dateEl).isValid();
}

export function getParsedISODate(date: string): string {
  return `${date}T01:00:00Z`;
}

export function isBlobArray(array: any[]): array is Blob[] {
  return array.every(value => value instanceof Blob);
}

/** Safe segment for a browser download filename (no path separators). */
export function sanitizeDownloadFilenamePart(value: string): string {
  return value
    .trim()
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

/** `{charity} - {title|Video Recording} - yyyy-mm-dd-HH-mm-ss.{ext}` for in-browser recordings. */
export function buildRecordedVideoDownloadFilename(
  charityName: string,
  postTitle: string | null | undefined,
  blob: Blob,
  recordedAt: Date = new Date()
): string {
  const ext = videoExtensionFromBlob(blob).replace(/^\./, "");
  const y = recordedAt.getFullYear();
  const m = String(recordedAt.getMonth() + 1).padStart(2, "0");
  const d = String(recordedAt.getDate()).padStart(2, "0");
  const h = String(recordedAt.getHours()).padStart(2, "0");
  const min = String(recordedAt.getMinutes()).padStart(2, "0");
  const s = String(recordedAt.getSeconds()).padStart(2, "0");
  const datePart = `${y}-${m}-${d}-${h}-${min}-${s}`;
  const charity = sanitizeDownloadFilenamePart(charityName) || "Charity";
  const title =
    sanitizeDownloadFilenamePart(postTitle || "") || "Video Recording";
  return `${charity} - ${title} - ${datePart}.${ext}`;
}

/** RTDB / Storage extension for a video blob (MediaRecorder may emit webm on Safari). */
export function videoExtensionFromBlob(blob: Blob): string {
  const type = blob.type || "";
  if (type.includes("webm")) {
    return ".webm";
  }
  if (type.includes("quicktime")) {
    return ".mov";
  }
  if (type.includes("3gpp")) {
    return ".3gp";
  }
  return ".mp4";
}

/** Infer stored video extension from a Firebase download URL or storage path segment. */
export function videoExtensionFromMediaUrl(url: string): string {
  const path = url.split("?")[0].toLowerCase();
  if (path.endsWith(".webm")) {
    return ".webm";
  }
  if (path.endsWith(".mov")) {
    return ".mov";
  }
  if (path.endsWith(".3gp")) {
    return ".3gp";
  }
  return ".mp4";
}

const CHROMIUM_MEDIA_RECORDER_UA = /chrome|chromium|crios|edg|opr/i;

const MP4_FIRST_RECORDER_MIME_TYPES = [
  "video/mp4",
  'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
  "video/webm;codecs=h264",
  'video/webm;codecs="vp8,opus"',
  "video/webm;codecs=vp9",
  "video/webm",
];

const WEBM_FIRST_RECORDER_MIME_TYPES = [
  'video/webm;codecs="vp8,opus"',
  "video/webm;codecs=h264",
  "video/webm;codecs=vp9",
  "video/webm",
  "video/mp4",
];

function isChromiumMediaRecorderEngine(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  return CHROMIUM_MEDIA_RECORDER_UA.test(navigator.userAgent);
}

/**
 * MIME type for Select Media in-browser recording (`react-video-recorder`).
 * Chromium prefers WebM; Safari and Firefox prefer MP4 when MediaRecorder supports it.
 */
export function preferredVideoRecorderMimeType(): string | undefined {
  if (typeof window === "undefined" || !window.MediaRecorder?.isTypeSupported) {
    return undefined;
  }
  const candidates = isChromiumMediaRecorderEngine()
    ? WEBM_FIRST_RECORDER_MIME_TYPES
    : MP4_FIRST_RECORDER_MIME_TYPES;
  return candidates.find(type => window.MediaRecorder.isTypeSupported(type));
}

export function isPreviewThumbnailReplacePayload(
  media: Array<Blob | string> | undefined
): media is [Blob, string] {
  return (
    !!media &&
    media.length === 2 &&
    media[0] instanceof Blob &&
    typeof media[1] === "string"
  );
}

export function moneyFormat(num: number): string {
  return num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
}

export const commaSeparated = moneyFormat;

export function commaSeparatedInt(num: number): string {
  return commaSeparated(num).split(".")[0];
}

export function dateFormat(date: number, options: {}): string {
  let keywordSubscribed = "";
  if (date) {
    keywordSubscribed += date
      ? new Date(date).toLocaleDateString("en-US", options)
      : "Not available";
  }

  return keywordSubscribed;
}
export function formatDate(date: number): string {
  const momentDate = moment.unix(date / 1000);

  if (Math.abs(moment().diff(momentDate, "hours")) < 24) {
    return momentDate.format("LT");
  } else if (Math.abs(moment().diff(momentDate, "days")) < 7) {
    return momentDate.format("dddd");
  }

  return momentDate.format("L");
}

export function formatPhoneNumber(phone: string): string {
  if (phone) {
    const numberWithFormat = /^\(?[0-9]{3}\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

    if (numberWithFormat.test(phone)) {
      return phone;
    }

    const digits = phone.replace(/\D/g, "");
    const input =
      digits.length === 11 && digits.startsWith("1")
        ? digits.substring(1)
        : digits;
    const zip = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);

    if (input.length > 6) {
      return `(${zip}) ${middle}-${last}`;
    } else if (input.length > 3) {
      return `(${zip}) ${middle}`;
    } else if (input.length > 0) {
      return `${zip}`;
    }
    return phone;
  }
  return phone;
}

export function phonePlainFormat(phone: string): string {
  if (phone) {
    return phone.replace(/\D+/g, "");
  }

  return phone;
}

export function formatCalendarDate(date: number): string {
  const momentDate = moment.unix(date / 1000);
  return momentDate.calendar();
}

/** Local calendar day label for Messenger thread separators. */
export function formatMessengerDayLabel(date: number): string {
  const momentDate = moment.unix(date / 1000);
  const day = momentDate.clone().startOf("day");
  const today = moment().startOf("day");
  const dayDiff = today.diff(day, "days");
  if (dayDiff === 0) {
    return "Today";
  }
  if (dayDiff === 1) {
    return "Yesterday";
  }
  return momentDate.format("dddd, MMMM D, YYYY");
}

/** Compact local time for a Messenger bubble. */
export function formatMessengerMessageTime(date: number): string {
  return moment.unix(date / 1000).format("h:mm A");
}

/** True when both timestamps fall on the same local calendar day. */
export function isSameLocalCalendarDay(
  firstDate: number | undefined,
  secondDate: number | undefined
): boolean {
  if (firstDate == null || secondDate == null) {
    return false;
  }
  return moment
    .unix(firstDate / 1000)
    .isSame(moment.unix(secondDate / 1000), "day");
}

/** Day-separator copy, or null when this bubble should not start a new day. */
export function messengerDaySeparatorLabel(
  messageDate: number | undefined,
  previousDate: number | undefined
): string | null {
  if (messageDate == null) {
    return null;
  }
  if (isSameLocalCalendarDay(previousDate, messageDate)) {
    return null;
  }
  return formatMessengerDayLabel(messageDate);
}

export function getLastMessageDate(chat: IChat): string {
  if (chat.messageList.length == 0) {
    return "";
  }
  const date = chat.messageList[chat.messageList.length - 1].date;
  return formatCalendarDate(date);
}

export function getImageMediaKey(media: MediaList): string | null {
  if (media) {
    return (
      Object.keys(media)
        .filter(key => media[key].type === "image")
        .shift() || null
    );
  }

  return null;
}

export function getVideoMediaKey(media: MediaList): string | null {
  if (media) {
    return (
      Object.keys(media)
        .filter(key => media[key].type === "video")
        .shift() || null
    );
  }

  return null;
}

/**
 * Resolves the poster/list thumbnail URL for a post. Prefers processed URLs on
 * the video preview image (`{videoKey}__preview`) before raw `mediaUrl`; on
 * production deployments, alpha-storage `mediaUrl` is skipped when processed
 * URLs exist. Falls back to legacy `thumbnail` on media nodes, then any image
 * `mediaUrl`.
 */
export function getPostThumbnailDisplayUrl(post: IPost): string {
  if (!post?.media) {
    return "";
  }
  const media = post.media;
  const videoKey = getVideoMediaKey(media);
  const previewKey = videoKey ? `${videoKey}__preview` : null;
  if (videoKey && previewKey) {
    const preview = media[previewKey];
    if (preview?.type === "image") {
      const previewUrl = pickPreviewThumbnailUrl(preview);
      if (previewUrl) {
        return previewUrl;
      }
    }
  }
  for (const id of Object.keys(media)) {
    if (id === previewKey) {
      continue;
    }
    const m = media[id];
    if (m?.thumbnail) {
      return m.thumbnail;
    }
  }
  for (const id of Object.keys(media)) {
    if (id === previewKey) {
      continue;
    }
    const m = media[id];
    if (m?.type === "image" && m.mediaUrl) {
      return m.mediaUrl;
    }
  }
  return "";
}

export function postHasCustomThumbnail(post: IPost): boolean {
  if (!post?.media) {
    return false;
  }
  const videoKey = getVideoMediaKey(post.media);
  if (!videoKey) {
    return false;
  }
  const preview = post.media[`${videoKey}__preview`];
  return preview?.type === "image" && preview.customThumbnail === true;
}

export function nodeForPostType(type: PostType): string {
  return type === "pending" ? "pendingPosts" : "publishedPosts";
}

// "onlySms" | "onlyMms" | "news" | "informative";
export function postOnlySMS(postStyle: PostStyle): boolean {
  return ["onlySms", "onlyMms"].includes(postStyle);
}

export function smsChannelLabel(postType?: PostStyle): "SMS" | "MMS" {
  return postType === "onlySms" ? "SMS" : "MMS";
}

/**
 * Builds an {@link IMedia} payload for RTDB. When `previous` is set and
 * `previous.mediaUrl` equals `url`, `uploaded` and `processed` are preserved so
 * saving does not reset pipeline state for an unchanged asset (e.g. video when
 * only the preview image changes).
 */
export function getMediaObject(
  type: "video" | "image",
  url: string,
  previous?: IMedia | null
): IMedia {
  const extension =
    type === "video" ? videoExtensionFromMediaUrl(url) : ".jpeg";
  const out: IMedia = {
    mediaUrl: url,
    type,
    uploaded: true,
    processed: false,
    extension,
  };
  if (previous && previous.mediaUrl === url) {
    out.uploaded = previous.uploaded;
    out.processed = previous.processed;
  }
  return out;
}

export function getDefaultPostMedia(): IMedia {
  const imageURL =
    "https://firebasestorage.googleapis.com/v0/b/viewspark-production.appspot.com/o/generalAssets%2Fonlysms.jpg?alt=media&token=e58fd303-bf91-454e-bdca-b5e63170e83b";
  let defaultMedia = {
    ...getMediaObject("image", imageURL),
    processed: true,
    uploaded: true,
    defaultMedia: true,
    thumbnail: imageURL,
  };

  return defaultMedia;
}

export async function getCharityLogoPostMedia(
  charityId: string
): Promise<IMedia> {
  let imageURL = `https://storage.googleapis.com/public-viewspark-production/CharityLogos/${charityId}.jpg`;

  let charityLogoMedia = {
    ...getMediaObject("image", imageURL),
    processed: true,
    uploaded: true,
    defaultMedia: true,
    thumbnail: imageURL,
  };

  return charityLogoMedia;
}

export function validURL(str): boolean {
  var pattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
    "(([a-z\\d](-*[a-z\\d])*\\.)+[a-z]{2,}|" + // domain name
    "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
    "(\\:\\d+)?(\\/[\\/a-z\\d%_.~+#-]*)*" + // port and path
    "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // fragment locator
  return !!pattern.test(str);
}

export function validURLv2(string) {
  try {
    new URL(string);
    return true;
  } catch (err) {
    return false;
  }
}

export function isDonorDeactivated(donor: IUser): boolean {
  return donor.deactivated == undefined ? false : donor.deactivated;
}

export function donorHasNotifications(donor: IUser): boolean {
  const hasNotificationsNode = donor.notifications != null;
  const notificationEmail =
      !hasNotificationsNode || get(donor, "notifications.email", false),
    notificationSMS =
      !hasNotificationsNode || get(donor, "notifications.sms", false),
    hasEmail = get(donor, "email", false),
    hasPhone = get(donor, "phoneNumber", false);
  return (notificationEmail && hasEmail) || (notificationSMS && hasPhone);
}

export function getActiveMedium(donor: IUser): string {
  if (donor.notifications.sms && donor.phoneNumber) return donor.phoneNumber;
  if (donor.notifications.email && donor.email) return donor.email;
  return "None";
}

function audienceInt(raw: Record<string, unknown>, camel: string, snake: string): number {
  const value = raw[camel] ?? raw[snake];
  return typeof value === "number" ? value : 0;
}

export function normalizeAudienceMetrics(raw: unknown): IAudienceMetrics {
  const source =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    missing: audienceInt(source, "missing", "missing"),
    deactivated: audienceInt(source, "deactivated", "deactivated"),
    bothOff: audienceInt(source, "bothOff", "both_off"),
    unsubscribed: audienceInt(source, "unsubscribed", "unsubscribed"),
    noContact: audienceInt(source, "noContact", "no_contact"),
    noReachable: audienceInt(source, "noReachable", "no_reachable"),
    duplicated: audienceInt(source, "duplicated", "duplicated"),
    sms: audienceInt(source, "sms", "sms"),
    email: audienceInt(source, "email", "email"),
  };
}

export function getCouldNotSend(metrics: {
  noContact: number;
  noReachable: number;
  bothOff: number;
  missing: number;
}): number {
  return (
    metrics.noContact +
    metrics.noReachable +
    metrics.bothOff +
    metrics.missing
  );
}

export function getTargetedDonors(post: IPost): number {
  const totalSmsNotifications =
    post?.notificationsCount?.sms !== undefined &&
    post?.notificationsCount?.sms !== null
      ? post.notificationsCount.sms
      : 0;
  const totalEmailNotifications =
    post?.notificationsCount?.email !== undefined &&
    post?.notificationsCount?.email !== null
      ? post.notificationsCount.email
      : 0;
  return totalSmsNotifications + totalEmailNotifications;
}

export function postHasIncompleteMedia(post: IPost | undefined): boolean {
  if (!post || !post.media) {
    return false;
  }
  for (const m of Object.values(post.media)) {
    if (!m || !m.uploaded || !m.processed) {
      return true;
    }
  }
  return false;
}

/**
 * True when post media is still uploading or has not finished processing.
 */
export function isPostMediaUpdating(post: IPost | undefined): boolean {
  if (!post) {
    return false;
  }
  if (post.status === "uploading" || post.status === "processing") {
    return true;
  }
  return postHasIncompleteMedia(post);
}

/**
 * True when a delivered published ViewSpark has replacement media still
 * processing on the published node or on a pending twin of the same id.
 */
export function isDeliveredPostMediaUpdating(
  published: IPost | undefined,
  pendingTwin?: IPost
): boolean {
  if (!published || published.status !== "delivered") {
    return false;
  }
  return postHasIncompleteMedia(published) || isPostMediaUpdating(pendingTwin);
}

/**
 * Strips one or more leading line breaks from {@code text} so UI does not show
 * an empty first line when upstream payloads include a spurious {@code \n} or {@code \r\n}.
 */
export function stripLeadingLineBreaks(text: string): string {
  if (!text) {
    return text;
  }
  return text.replace(/^[\r\n]+/, "");
}
